import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { showPrompt } from '../../utils/confirmUtils';
import { toastSuccess, toastError } from '../../utils/toasts';
import SignatureWorkflowModal from './SignatureWorkflowModal';
import { IconUsb, IconCircleCheck } from '@tabler/icons-react';

const InvoiceSignatureButton = ({ facture, onUpdateFacture }) => {
  const [loadingUsb, setLoadingUsb] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || 'https://app.accountintime.tn';

  if (facture.eInvoicingStatus === 'SIGNED') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <IconCircleCheck size={14} className="mr-1" /> Signée
        {facture.eInvoicingSignedXml ? '' : ' (Locale)'}
      </span>
    );
  }

  const handleUsbSignClick = () => setIsModalOpen(true);

  // === Appelé après les 3 étapes du modal ===
  // signatureData = { role, consented, policy, certificateId, certificateInfo }
  const handleSignConfirmed = async (signatureData) => {
    setIsModalOpen(false);
    const { role, consented, policy, certificateId, certificateInfo } = signatureData || {};

    try {
      setLoadingUsb(true);
      const token = localStorage.getItem('token');
      const workspaceId = localStorage.getItem('active_workspace_id');

      const commonHeaders = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      if (workspaceId) commonHeaders['x-workspace-id'] = workspaceId;

      // --- Étape A : Récupérer le XML TEIF brut ---
      const xmlResp = await fetch(`${apiUrl}/api/invoices/${facture.id || facture._id}/generate-xml`, {
        method: 'GET',
        headers: commonHeaders
      });
      if (!xmlResp.ok) {
        const errorData = await xmlResp.json().catch(() => null);
        throw new Error(errorData?.error || errorData?.message || "Erreur lors de la génération du XML");
      }
      const dataXml = await xmlResp.json();
      const rawXml = dataXml.xmlContent || dataXml.xml || dataXml;

      // --- Étape B : Demander le PIN (après sélection du certificat) ---
      const pinMessage = certificateInfo
        ? `Saisir le PIN pour signer avec :\n"${certificateInfo.subjectCN || certificateInfo.label}"\n(émis par ${certificateInfo.issuerCN || 'N/A'})`
        : "Veuillez saisir le PIN de votre clé USB Token :";
      const pin = await showPrompt(pinMessage);
      if (!pin) return;

      // --- Étape C : Agent local — avec certId + role + consent ---
      const agentResp = await fetch('http://localhost:5050/sign-xml', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          xml: rawXml,
          pin: pin,
          role: role,
          consent: {
            accepted: !!consented,
            policy: policy,
            acceptedAt: new Date().toISOString(),
          },
          certificateId: certificateId,  // ← le certificat choisi à l'étape 3
        })
      }).catch(() => {
        throw new Error("Impossible de communiquer avec la clé USB. Vérifiez que l'agent tourne sur le port 5050.");
      });

      if (!agentResp.ok) {
        const errData = await agentResp.json().catch(() => null);
        throw new Error(errData?.error || "Erreur de communication avec l'agent local.");
      }

      const agentData = await agentResp.json();
      if (!agentData.success || !agentData.signedXml) {
        throw new Error(agentData.error || "Échec de la signature locale (PIN incorrect ?)");
      }

      // === Capture du temps de signature (point 3 ANCE) ===
      const signedAt = new Date();
      const signedAtFormatted = signedAt.toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      });

      // --- Étape D : Sauvegarde backend ---
      const uploadResp = await fetch(`${apiUrl}/api/invoices/${facture.id || facture._id}/upload-signed`, {
        method: 'POST',
        headers: commonHeaders,
        body: JSON.stringify({
          signedXml: agentData.signedXml,
          signedAt: signedAt.toISOString(),
          signerRole: role,
          certificateId: certificateId,
        })
      });
      if (!uploadResp.ok) {
        const errorData = await uploadResp.json().catch(() => null);
        throw new Error(errorData?.error || "Erreur lors de la sauvegarde du fichier signé");
      }

      toastSuccess("Signature USB réussie !");

      if (onUpdateFacture) {
        onUpdateFacture({
          ...facture,
          eInvoicingStatus: 'SIGNED',
          eInvoicingSignedXml: agentData.signedXml,
          eInvoicingSignedAt: signedAt.toISOString(),
          eInvoicingSignerRole: role,
        });
      }

    } catch (error) {
      console.error("Erreur USB:", error);
      toastError(error.message);
    } finally {
      setLoadingUsb(false);
    }
  };

  return (
    <>
      <button
        onClick={handleUsbSignClick}
        disabled={loadingUsb}
        className={`p-1.5 rounded-lg transition-colors border ${loadingUsb ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-50 border-blue-200'}`}
        title="Signer avec Clé USB (Token)"
      >
        <IconUsb size={14} strokeWidth={1.6} />
      </button>
      <SignatureWorkflowModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleSignConfirmed}
        facture={facture}
        apiUrl={apiUrl}
      />
    </>
  );
};

InvoiceSignatureButton.propTypes = {
  facture: PropTypes.object.isRequired,
  onUpdateFacture: PropTypes.func,
};

export default InvoiceSignatureButton;
